import { useDispatch, useSelector } from "react-redux";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "primereact/tooltip";
import { Menu } from "primereact/menu";
import EnhancedCheckbox from "../../../SharedComponents/EnhancedCheckbox";
import QuickShowWizard from "../../../SharedComponents/QuickShow/QuickShowWizard";

import { formatDuration, formatSize, getTitle } from "../../../../config/global";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { deleteItemLocally } from "../../../../redux/Slices/scrollSlice";
import { useGalleryContext } from "../../../Contexts/GalleryContext";
import DeleteConfirmation from "../../../SharedComponents/DeleteConfirmation";
import { useDeleteMedia, useGetMediaUsage } from "../../../../queries/media";
import MoveMediaFolder from "../FolderDragDrop/MoveMediaFolder";
import UpdateMedia from "./UpdateMedia";
import LazyImage from "../../../SharedComponents/ShowMedia/LazyImage";
import DeleteMediaDialog from "./DeleteMediaDialog";
import { SplitButton } from "primereact/splitbutton";
import UploadToPlaylist from "./UploadToPlaylist";
import { classNames } from "primereact/utils";
import { formatDateTime } from "../../../../utils/datetime";

function MediaCard({
  item,
  displayAction = true,
  forceMobileActions = false,
  showCategoryIcon = false,
  showMobileDuration = false,
  // Explicit overrides for callers that need MediaCard's real "grid" mode
  // (actual thumbnail, click-to-preview) WITHOUT touching the app-wide
  // GalleryContext — e.g. the campaign builder's add-content media picker,
  // which has its own selection state separate from the real Media
  // Gallery's idsList. When provided, these win over context entirely for
  // this instance; every existing caller passes neither and is unaffected.
  layoutOverride = null,
  selectedOverride = null,
  onToggleOverride = null,
  // The grid thumbnail's hover "Show on Screen" overlay button is real,
  // wanted functionality on the real Media Gallery — but inside a small
  // picker tile it covers the thumbnail entirely and duplicates the same
  // action already offered inside the preview dialog. Opt-out only; every
  // existing caller keeps the overlay exactly as before.
  hideQuickShowOverlay = false,
}) {
  const { layout: contextLayout, idsList, setIdsList, multiSelectCheck, selectedItem, setSelectedItem } = useGalleryContext();
  const layout = layoutOverride || contextLayout;
  const isSelectionOverridden = selectedOverride !== null;
  const checkboxChecked = isSelectionOverridden ? selectedOverride : idsList.indexOf(`${item.id}`) !== -1;
  const showCheckbox = isSelectionOverridden || multiSelectCheck;
  const { openDialog, deleteDialog } = useSelector((state) => state.global);
  const deleteMedia = useDeleteMedia();
  const dispatch = useDispatch();
  const menu = useRef(null);
  const fetchMediaUsage = useGetMediaUsage();
  const { t } = useTranslation();
  // Quick Show wizard for this media item. Local-state controlled so each
  // card instance opens its own dialog without leaking through Redux.
  const [quickShowOpen, setQuickShowOpen] = useState(false);

  const options = [
    {
      label: "Options",
      items: [
        // "Show on Screen" sits at the top — it's the highest-intent action.
        {
          label: t("quick_show.title"),
          icon: "pi pi-desktop",
          command: () => setQuickShowOpen(true),
        },
        {
          label: t('buttons.update'),
          icon: "pi pi-refresh",
          command: () => {
            popupHandler("updateMedia");
          },
        },
        {
          label: t('buttons.delete'),
          icon: "pi pi-times",
          command: () => {
            deletePopupHandler();
          },
        },
        {
          label: t('media.media_move'),
          icon: "pi pi-arrow-right",
          command: () => {
            popupHandler("moveMediaFolder");
          },
          visible: getTitle() === "Media",
        },
      ],
    },
  ];

  const splitBtnOptions = [
    {
      label: t("quick_show.title"),
      icon: "pi pi-desktop",
      command: () => setQuickShowOpen(true),
    },
    {
      label: t('media.media_add_to_playlists'),
      icon: "fa-solid fa-layer-group",
      command: () => {
        popupHandler("uploadToPlaylist");
      },
    },
    {
      label: t('buttons.update'),
      icon: "pi pi-refresh",
      command: () => {
        popupHandler("updateMedia");
      },
    },
    {
      label: t('media.media_move'),
      icon: "pi pi-arrow-right",
      classNames: "bg-red-500",
      command: () => {
        popupHandler("moveMediaFolder");
      },
      visible: getTitle() === "Media",
    },
    {
      label: t('buttons.delete'),
      icon: "pi pi-trash",
      command: () => {
        deletePopupHandler();
      },
    },
  ];

  const getCategoryMeta = (type) => {
    switch (String(type || "").toUpperCase()) {
      case "EXTERNAL_LINK":
        return { icon: "fas fa-arrow-up-right-from-square", label: t('media.media_type_external_link') };
      case "YOUTUBE":
        return { icon: "fab fa-youtube", label: "YouTube" };
      case "DRIVE":
        return { icon: "fab fa-google-drive", label: "Drive" };
      case "WEBVIEW":
        return { icon: "fas fa-globe", label: t('media.media_type_webview') };
      case "IMAGE":
      case "CORNER":
        return { icon: "far fa-image", label: t('media.media_type_image') };
      case "AUDIO":
        return { icon: "fas fa-music", label: t('media.media_type_audio') };
      case "VIDEO":
        return { icon: "fas fa-video", label: t('media.media_type_video') };
      case "3D":
        return { icon: "fas fa-cube", label: "3D" };
      default:
        return { icon: "fas fa-link", label: t('media.media_type_link') };
    }
  };

  const formatTypeLabel = (type) => {
    const normalized = String(type || "").toUpperCase();
    if (normalized === "CORNER") {
      return "image";
    }

    return normalized.toLowerCase().replace(/_/g, " ");
  };

  const categoryMeta = getCategoryMeta(item.type);

  const deleteHandler = async () => {
    await deleteMedia.mutateAsync(
      { id: item.id },
      {
        onSuccess: () => {
          dispatch(deleteItemLocally(item.id));
          dispatch(toggleDialog("deleteMediaDialog"));
        },
      }
    );
  };

  // On Media Checkbox Change
  const onMediaChange = (e) => {
    let selectedMedia = [...idsList];

    if (e.checked) {
      selectedMedia.push(e.value);
    } else {
      selectedMedia.splice(selectedMedia.indexOf(e.value), 1);
    }

    setIdsList(selectedMedia);
  };

  const popupHandler = (dialogKey) => {
    setSelectedItem(item.id);
    dispatch(toggleDialog(dialogKey));
  };

  const deletePopupHandler = () => {
    setSelectedItem(item.id);

    fetchMediaUsage.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          dispatch(toggleDialog("deleteMediaDialog"));
        },
        onError: (error) => {
          console.error("Failed to fetch media usage:", error);
        },
      }
    );
  };

  const durationColClass = classNames("fs-6 text-center", {
    "col-2 col-md-1 d-none d-sm-block": !showMobileDuration,
    "col-2 col-md-1": showMobileDuration,
  });

  // For non-video/audio media, the duration column reads "--" which is
  // noisy. If we have image dimensions on the `info` JSON field, show
  // them instead. Otherwise just blank the cell out so the row stays
  // clean instead of repeating "--".
  const isTimed = ["VIDEO", "AUDIO", "YOUTUBE", "DRIVE", "MULTI_3D"].includes(item.type);
  const renderDurationCell = () => {
    if (isTimed) return formatDuration(item.duration);
    const w = item?.info?.width;
    const h = item?.info?.height;
    if (w && h) return <span className="text-muted">{w}×{h}</span>;
    return <span className="text-muted">—</span>;
  };

  const sizeColClass = classNames("col-3 col-sm-2 col-md-2 fs-6 text-truncate text-center", {
    "d-none d-md-block": showMobileDuration,
  });

  return (
    <>
      {layout === "grid" ? (
        <>
          {/* Fixed 160px-tall thumbnail with object-fit: cover so portrait
              + landscape media never distorts the card height. Wrapped in
              position-relative so the hover "Show on Screen" overlay can
              anchor to the top-left corner. */}
          <div className="pp-thumb-grid position-relative">
            <LazyImage
              media={item}
              popupType="media_preview"
              styleClass="w-100 h-100"
            />
            {!hideQuickShowOverlay && (
              <button
                type="button"
                className="p-button p-button-sm pp-show-on-screen-grid"
                onClick={(e) => { e.stopPropagation(); setQuickShowOpen(true); }}
                aria-label={t('quick_show.title')}
                title={t('quick_show.title')}
                style={{ minHeight: 36 }}
              >
                <i className="pi pi-desktop me-2" />
                {t('quick_show.title')}
              </button>
            )}
          </div>
          <div className="fs-6 w-100 text-truncate text-center mt-3" title={(item.file_name || "").replace(/_/g, " ")}>
            {showCategoryIcon && <i className={`${categoryMeta.icon} me-2`} aria-hidden="true"></i>}
            {(item.file_name || "").replace(/_/g, " ")}
          </div>
          <div className="fs-6 w-100 text-truncate text-center mt-2 fw-light fs-8">{formatDateTime(item.created_at)}</div>
          <span
            className={`position-absolute text-center ${multiSelectCheck ? "media_action_left" : "media_action_right"}`}
            onClick={(event) => menu.current.toggle(event)}
          >
            <i
              className="fas fa-ellipsis-h text-light fs-4"
              tooltip="option"
              tooltipOptions={{ position: "bottom" }}
              onClick={(event) => menu.current.toggle(event)}
              aria-controls="popup_menu"
              aria-haspopup
            ></i>
            <Menu model={options} popup ref={menu} id="popup_menu" />
          </span>
          {showCheckbox && (
            <EnhancedCheckbox
              inputId={item.id}
              name="media"
              className="grid_media_checkbox"
              value={item.id}
              onChange={onToggleOverride || onMediaChange}
              checked={checkboxChecked}
            />
          )}
        </>
      ) : (
        <>
          {!displayAction ? (
            <>
              {/* Fixed 64×48 thumbnail — width is the inline style, NOT a
                  Bootstrap col, so it never widens on large screens. */}
              <div className="pp-thumb-list me-2" style={{ flexShrink: 0 }}>
                <LazyImage
                  media={item}
                  popupType="media_preview"
                  styleClass="w-100 h-100"
                />
              </div>
              <div className="col text-truncate pe-md-2 pe-lg-4 min-w-0" title={(item.file_name || "").replace(/_/g, " ")}>
                {showCategoryIcon && <i className={`${categoryMeta.icon} me-2`} aria-hidden="true"></i>}
                {(item.file_name || "").replace(/_/g, " ")}
              </div>
              <div className="col-1 col-md-2 col-lg-2 fs-6 text-center d-none d-lg-block">
                {showCategoryIcon && <i className={`${categoryMeta.icon} me-2`} aria-hidden="true"></i>}
                {formatTypeLabel(item.type)}
              </div>
              <div className="col-2 col-md-2 col-lg-2 fs-6 text-center d-none d-lg-block">
                {renderDurationCell()}
              </div>
              <div className="col-1 col-md-2 col-lg-2 fs-6 text-truncate text-center d-none d-md-block">
                {formatSize(item.size) === "0 bytes" ? "—" : formatSize(item.size)}
              </div>
            </>
          ) : (
            <>
              {/* Same fixed thumbnail — replaces col-2 col-sm-1 which scaled
                  badly on wide monitors. */}
              <div className="pp-thumb-list me-2" style={{ flexShrink: 0 }}>
                <LazyImage media={item} popupType="media_preview" styleClass="w-100 h-100" />
              </div>
              <div className="col text-truncate pe-md-4 min-w-0" title={(item.file_name || "").replace(/_/g, " ")}>
                {showCategoryIcon && <i className={`${categoryMeta.icon} me-2`} aria-hidden="true"></i>}
                {(item.file_name || "").replace(/_/g, " ")}
              </div>
              <div className={durationColClass}>
                {renderDurationCell()}
              </div>
              <div className="media_type col-1 col-md-2 fs-6 text-truncate text-center d-none d-sm-block">
                {showCategoryIcon && <i className={`${categoryMeta.icon} me-2`} aria-hidden="true"></i>}
                {formatTypeLabel(item.type)}
              </div>
              <div className={sizeColClass}>{formatSize(item.size) === "0 bytes" ? "—" : formatSize(item.size)}</div>
              <div className="col-2 col-md-2 fs-6 text-center d-none d-lg-block">{formatDateTime(item.created_at)}</div>
            </>
          )}

          {displayAction && (
            <div className="col-4 col-sm-3 col-md-3 col-xl-1 text-end d-flex align-items-center justify-content-end">
              <div className={classNames("actions", { "links-mobile-actions-visible": forceMobileActions })}>
                <div className="d-flex align-items-center gap-2">
                  <Tooltip target={`.delete-button-${item.id}`} className="fs-8" />

                  {/* Hover-visible "Show on Screen" icon — same action as
                      the top of the split-button menu, but surfaced inline
                      so phone + desktop users can hit it in one tap. */}
                  <button
                    type="button"
                    className="btn btn-sm btn-icon touch-target mobile-card-action pp-show-on-screen-list"
                    aria-label={t('quick_show.title')}
                    title={t('quick_show.title')}
                    onClick={(e) => { e.stopPropagation(); setQuickShowOpen(true); }}
                    style={{ color: 'var(--pp-btn-honey)' }}
                  >
                    <i className="pi pi-desktop"></i>
                  </button>

                  <button
                    className={`btn btn-sm btn-icon delete-button-${item.id} touch-target mobile-card-action`}
                    data-pr-position="bottom"
                    data-pr-tooltip="delete"
                    onClick={() => {
                      deletePopupHandler();
                    }}
                  >
                    <i className="bx bx-trash text-light"></i>
                  </button>

                  <SplitButton
                    label={null}
                    model={splitBtnOptions}
                    menuClassName="media_card_splitBtn"
                    className="p-button-text p-button-primary p-button-sm p-button-secondary touch-target mobile-card-action"
                    buttonClassName="d-none"
                    dropdownIcon="fas fa-ellipsis-v"
                  />
                </div>
              </div>

              {multiSelectCheck && (
                <EnhancedCheckbox
                  inputId={item.id}
                  name="media"
                  className="media_checkbox mx-1 mx-sm-3"
                  value={item.id}
                  onChange={onMediaChange}
                  checked={idsList.indexOf(`${item.id}`) !== -1}
                />
              )}
            </div>
          )}
        </>
      )}

      {deleteDialog.media && selectedItem === item.id && (
        <DeleteConfirmation isLoading={deleteMedia.isLoading} itemName={item.file_name} deleteHandler={deleteHandler} dialogKey="media" />
      )}

      <QuickShowWizard
        open={quickShowOpen}
        onClose={() => setQuickShowOpen(false)}
        media={{
          id: item.id,
          title: item.title || (item.file_name || '').replace(/_/g, ' '),
          file_name: item.file_name,
          thumb: item.thumb || item.thumbnail,
          type: item.type,
          duration: item.duration,
        }}
      />

      {openDialog.updateMedia && selectedItem === item.id && <UpdateMedia item={item} />}
      {openDialog.uploadToPlaylist && selectedItem === item.id && <UploadToPlaylist item={item} />}

      {openDialog.moveMediaFolder && selectedItem === item.id && <MoveMediaFolder data={item} />}
      {openDialog.deleteMediaDialog && selectedItem === item.id && fetchMediaUsage?.data && (
        <DeleteMediaDialog
          usage={fetchMediaUsage?.data}
          isLoading={deleteMedia.isLoading || fetchMediaUsage.isLoading}
          deleteHandler={deleteHandler}
          data={item}
        />
      )}
    </>
  );
}

export default MediaCard;

